import {Box, Button, IconButton, Popover, Typography} from "@mui/material";
import {FormattedMessage} from "react-intl";
import * as React from "react";
import {useState} from "react";
import FileSelector from "@/components/FileSelector";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {ErrorWithContext, MultipleLineError} from "@dvrt/graphql-widgets";
import {DomainImageInput} from "@/api";

type Props = {
    image: DomainImageInput & {url?: string};
    disabled: boolean;
    onChange: (newMedia: DomainImageInput) => void;
    errors?: ErrorWithContext[];
};

export const ImageRow = ({image, disabled, onChange, errors}: Props) => {
    const [showImage, setShowImage] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);

    const hasImageFile = !!(image && image.file);
    const hasImageUrl = !!(image && image.url && !image.unset);
    const hasImage = hasImageFile || hasImageUrl;

    function handleSelectFile(file: File | null) {
        if (file) {
            onChange({
                file,
                description: file.name,
                unset: false,
                id: null,
            });
        }
    }

    function handleRemoveFile() {
        onChange({
            file: null,
            description: "",
            unset: true,
            id: image.id,
        });
    }

    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
        setAnchorEl(event.currentTarget);
        setShowImage(true);
    }

    function handleClose() {
        setAnchorEl(null);
        setShowImage(false);
    }

    if (!hasImage)
        return (
            <FileSelector
                acceptFileType="image/*"
                onChange={handleSelectFile}
                color="primary"
                variant="contained"
                disabled={disabled}
            >
                <FormattedMessage id="upload" defaultMessage="Upload" />
            </FileSelector>
        );

    return (
        <Box>
            <Box display="flex" alignItems="center">
                <ImageDisplay image={image} disabled={disabled} handleClick={handleClick} />
                <IconButton color="secondary" disabled={disabled} onClick={() => handleRemoveFile()} size="large">
                    <FontAwesomeIcon icon="trash" />
                </IconButton>
            </Box>
            <Popover
                open={showImage}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                }}
            >
                <img src={image.url} alt={image.description} style={{maxWidth: "640px", maxHeight: "480px"}} />
            </Popover>
            <MultipleLineError errors={errors} />
        </Box>
    );
};

type ImageDisplayProps = {
    image: DomainImageInput & {url?: string};
    disabled: boolean;
    handleClick: React.MouseEventHandler<HTMLButtonElement>;
};

const ImageDisplay: React.FC<ImageDisplayProps> = ({image, disabled, handleClick}) => {
    if (image.file) return <Typography>{image.description}</Typography>;
    else if (image.url)
        return (
            <Button
                color="primary"
                variant="contained"
                disabled={disabled}
                style={{marginRight: "15px"}}
                onClick={handleClick}
            >
                <FormattedMessage id="domainSettings.showImage" defaultMessage="Show Image" />
            </Button>
        );

    console.warn("ImageDisplay: no image to display", image);
    return null;
};
